import React from 'react';

import type { Text } from '@zillow/constellation';
import {
  AdornedInput,
  Adornment,
  FormField,
  FormHelp,
  Grid,
  IconLockOutline,
  Input,
  Label,
  Paragraph,
  Spacer,
  Textarea,
} from '@zillow/constellation';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import type { HTMLElementEvent } from '../../../models/Types';
import PeopleInformation from './PeopleInformation';
import { LANDLORD_INFORMATION, PERSON } from '../../../constants/index';
import { PERSON_TYPE } from '../../../constants/stringConstants';
import type { PeopleSection } from '../../../models/index';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';

type Props = {
  section: PeopleSection;
  setSection: (section: PeopleSection) => void;
  setSectionValues: (keyValuePairs: object) => void;
};

export function LandlordInformation_v2({ section, setSection, setSectionValues }: Props): JSX.Element {
  const { user } = useRentalManagerUser();
  const { email: landlordEmail, firstName: landlordFirstName, lastName: landlordLastName } = user;
  const maxPeople = 1;

  function onFirstNameChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LANDLORD_INFORMATION.FIELD_NAME.FIRST_NAME]: value,
    });
  }

  function onLastNameChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LANDLORD_INFORMATION.FIELD_NAME.LAST_NAME]: value,
    });
  }

  function onAddressChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LANDLORD_INFORMATION.FIELD_NAME.ADDRESS]: value,
    });
  }

  function onPhoneNumberChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER]: value,
    });
  }

  return (
    <>
      <Paragraph fontType="h6" marginBottom="sm">
        Who is the landlord?
      </Paragraph>
      <Grid
        className={FULLSTORY_EXCLUDE}
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gap="sm"
        marginBottom="sm"
      >
        <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
          <FormField
            label={<Label id="landlord-first-name">{PERSON.LABEL.FIRST_NAME}</Label>}
            control={
              <Input
                aria-labelledby="landlord-first-name"
                value={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.FIRST_NAME)}
                onChange={onFirstNameChange}
              />
            }
          />
        </Grid>
        <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
          <FormField
            label={<Label id="landlord-last-name">{PERSON.LABEL.LAST_NAME}</Label>}
            control={
              <Input
                aria-labelledby="landlord-last-name"
                value={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.LAST_NAME)}
                onChange={onLastNameChange}
              />
            }
          />
        </Grid>
        {section.hasField(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS) && (
          <Grid gridColumnEnd="span 12">
            <FormField
              error={
                section
                  .getField(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS)
                  .showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)
                  ? true
                  : false
              }
              label={<Label id="landlord-mailing-address">{PERSON.LABEL.MAILING_ADDRESS_IMPORTANT}</Label>}
              control={
                <Textarea
                  resize="vertical"
                  aria-labelledby="landlord-mailing-address"
                  value={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS)}
                  onChange={onAddressChange}
                />
              }
              description={
                section
                  .getField(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS)
                  .showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) && (
                  <FormHelp>
                    {section
                      .getField(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS)
                      .showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)}
                  </FormHelp>
                )
              }
            />
          </Grid>
        )}
        <Grid gridColumnEnd="span 12">
          <FormField
            label={<Label>{PERSON.LABEL.EMAIL}</Label>}
            control={
              <AdornedInput
                readOnly
                rightAdornment={
                  <Adornment>
                    <IconLockOutline />
                  </Adornment>
                }
                input={<Input defaultValue={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.EMAIL)} />}
              />
            }
            marginBottom="xs"
          />
        </Grid>
        {section.hasField(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER) && (
          <Grid gridColumnEnd="span 12">
            <FormField
              error={
                section
                  .getField(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER)
                  .showOnError(PERSON.ERROR_MESSAGE.PHONE_NUMBER)
                  ? true
                  : false
              }
              label={<Label id="landlord-phone-number">{PERSON.LABEL.PHONE_NUMBER}</Label>}
              control={
                <Input
                  aria-labelledby="landlord-phone-number"
                  value={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER)}
                  onChange={onPhoneNumberChange}
                />
              }
              description={
                section
                  .getField(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER)
                  .showOnError(PERSON.ERROR_MESSAGE.PHONE_NUMBER) && (
                  <FormHelp>
                    {section
                      .getField(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER)
                      .showOnError(PERSON.ERROR_MESSAGE.PHONE_NUMBER)}
                  </FormHelp>
                )
              }
            />
          </Grid>
        )}
      </Grid>
      <Label>Does anyone else co-own the property?</Label>
      <Spacer marginBottom="xs" />
      <PeopleInformation
        isOptional
        maximumPeople={maxPeople}
        initialPeople={0}
        personType={PERSON_TYPE.COLANDLORD}
        setSectionValues={setSectionValues}
        setSection={setSection}
        section={section}
        yesAndNoText="Most states require that you include all co-owners as co-landlords on your lease."
        initialEmail={landlordEmail}
        initialFirstName={landlordFirstName}
        initialLastName={landlordLastName}
      />
    </>
  );
}

export default LandlordInformation_v2;

LandlordInformation_v2.description = 'Colandlord information in addition to landlord information';
